import { Metric } from "@/app/types"
import { isStringNotEmpty } from "@/app/utils"

// A formatter will return HTML string for a given value object, to be used for display
export type FormatOutput = string | undefined
export type Formatter = (
  v: any,
  colIdx?: number,
  isHeader?: boolean
) => FormatOutput

// An exporter will return a primitive type - string/number for a given value object, to be used for data export, e.g. CSV
export type ExportOutput = string | number | boolean | undefined
export type Exporter = (
  v: any,
  colIdx?: number,
  isHeader?: boolean
) => ExportOutput

// https://tc39.es/ecma402/#sec-properties-of-intl-numberformat-instances
const twoDecimalsForced = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
  roundingMode: "halfTrunc",
})

const oneDecimalForced = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
  roundingMode: "halfTrunc",
})

const twoDecimals = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 1,
  maximumFractionDigits: 2,
  roundingMode: "halfTrunc",
})

const oneDecimal = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 1,
  roundingMode: "halfTrunc",
})

const noDecimal = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
})

export namespace Formatter {
  export function getFormatter(
    metricName: string,
    config: "CHART" | "TABLE" = "CHART",
    forceDecimals: boolean = true
  ): Formatter {
    if (!MetricFormatterMap.get(metricName))
      throw Error("Invalid metric name: " + metricName)

    return MetricFormatterMap.get(metricName)![config](forceDecimals)
  }
}

type PrefixFn = string | ((value: number) => string)
type SuffixFn = string | ((value: number) => string)
type NumStrFn = (value: number) => string

// factory function to generate a lambda Formatter for given prefix/numstr/suffix functions
function _create_formatter(
  prefix_fn: PrefixFn,
  suffix_fn: SuffixFn,
  numstr_fn: NumStrFn
) {
  return (value: any, _, isHeader: boolean) => {
    if (isHeader) return value
    if (value == null || !(typeof value == "number")) return "<span>N/A</span>"
    const abs_val = Math.abs(value)
    const sign = value < 0 ? "-" : ""
    const prefix =
      typeof prefix_fn === "function" ? prefix_fn(abs_val)
      : isStringNotEmpty(prefix_fn) ? prefix_fn
      : ""

    const numstr = typeof numstr_fn === "function" ? numstr_fn(abs_val) : ""

    const suffix =
      typeof suffix_fn === "function" ? suffix_fn(abs_val)
      : isStringNotEmpty(suffix_fn) ? suffix_fn
      : ""
    const txt = `${sign}${prefix}${numstr}${suffix}`
    return `<span class="">${txt}</span>`
  }
}

// helper function to "trim" down a value to the largest suffix
type _suffix = { val: number; suffix: string }
type _limit = { limit: number; divisor: number; suffix: string }

function _get_usd_suffix(
  value: number,
  limitMultiplier: number | null = null
): _suffix {
  // we use limit multiplier to adjust suffix logic e.g. 500k could become 0.5M
  // for dollars, change the limit for prefix "M" to 100k, so 150k becomes 0.15 M
  if (limitMultiplier && limitMultiplier <= 0 && limitMultiplier > 1)
    throw new Error("Invalid limit multiplier.")

  if (limitMultiplier === null) limitMultiplier = 0.5

  return _get_suffix(value, [
    { limit: 1_000, divisor: 1_000, suffix: " K" },
    { limit: 1_000_000 * limitMultiplier, divisor: 1_000_000, suffix: " M" },
    {
      limit: 1_000_000_000 * limitMultiplier,
      divisor: 1_000_000_000,
      suffix: " B",
    },
    {
      limit: 1_000_000_000_000 * limitMultiplier,
      divisor: 1_000_000_000_000,
      suffix: " T",
    },
  ])
}

function _get_num_suffix(value: number): _suffix {
  return _get_suffix(value, [
    { limit: 1_000, divisor: 1_000, suffix: " K" },
    { limit: 1_000_000, divisor: 1_000_000, suffix: " M" },
    { limit: 1_000_000_000, divisor: 1_000_000_000, suffix: " B" },
    { limit: 1_000_000_000_000, divisor: 1_000_000_000_000, suffix: " T" },
  ])
}

// this function should only be called with +ve numbers
function _get_suffix(value: number, suffixes: _limit[]): _suffix {
  let [val, suffix] = [value, ""]
  // if there are no suffixes, then return as-is
  if (!Array.isArray(suffixes) || suffixes.length == 0) return { val, suffix }

  // calc max suffix with 3 decimals, start with the highest suffix
  if (suffixes.length > 3 && value >= suffixes[3].limit)
    [val, suffix] = [
      Math.floor((value / suffixes[3].divisor) * 1000.0) / 1000.0,
      suffixes[3].suffix,
    ]
  else if (suffixes.length > 2 && value >= suffixes[2].limit)
    [val, suffix] = [
      Math.floor((value / suffixes[2].divisor) * 1000.0) / 1000.0,
      suffixes[2].suffix,
    ]
  else if (suffixes.length > 1 && value >= suffixes[1].limit)
    [val, suffix] = [
      Math.floor((value / suffixes[1].divisor) * 1000.0) / 1000.0,
      suffixes[1].suffix,
    ]
  else if (suffixes.length > 0 && value >= suffixes[0].limit)
    [val, suffix] = [
      Math.floor((value / suffixes[0].divisor) * 1000.0) / 1000.0,
      suffixes[0].suffix,
    ]
  else [val, suffix] = [value, ""]

  return { val, suffix }
}

const USD_FORMATTER_CHART = (forceDecimals: boolean) =>
  _create_formatter("$", "", (value) => {
    const s = _get_usd_suffix(value)
    return `${(forceDecimals ? oneDecimalForced : oneDecimal).format(s.val)}${s.suffix}`
  })

const USD_FORMATTER_TABLE = (forceDecimals: boolean) =>
  _create_formatter("$", "", (value) => {
    const s = _get_usd_suffix(value, 1)
    return `${(forceDecimals ? oneDecimalForced : oneDecimal).format(s.val)}${s.suffix}`
  })

const PCENT_ONE_FORMATTER = (forceDecimals: boolean) =>
  _create_formatter("", "%", (value) => {
    const s = _get_num_suffix(value)
    return `${(forceDecimals ? oneDecimalForced : oneDecimal).format(s.val)}${s.suffix}`
  })

const PCENT_TWO_FORMATTER = (forceDecimals: boolean) =>
  _create_formatter("", "%", (value) => {
    const s = _get_num_suffix(value)
    return `${(forceDecimals ? twoDecimalsForced : twoDecimals).format(s.val)}${s.suffix}`
  })

const NO_FORMATTER =
  (forceDecimals: boolean) => (value: any, _, isHeader: boolean) => {
    if (isHeader) return value
    return value < 0 ?
        `<span class="">-${value}</span>`
      : `<span>${value}</span>`
  }

const NUMBER_FORMATTER =
  (forceDecimals: boolean) => (value: any, _, isHeader: boolean) => {
    if (isHeader) return value
    if (value == null || !(typeof value == "number")) return "<span>N/A</span>"
    return `<span class="">${noDecimal.format(Math.floor(value))}</span>`
  }

const MetricFormatterMap = new Map([
  [Metric.NumFunds, { CHART: NUMBER_FORMATTER, TABLE: NUMBER_FORMATTER }],
  [Metric.AUM, { CHART: USD_FORMATTER_CHART, TABLE: USD_FORMATTER_TABLE }],
  [
    Metric.AumChange,
    { CHART: PCENT_ONE_FORMATTER, TABLE: PCENT_TWO_FORMATTER },
  ],
  [
    Metric.CumeNetflow,
    { CHART: USD_FORMATTER_CHART, TABLE: USD_FORMATTER_TABLE },
  ],
  [
    Metric.ExpenseRatio,
    { CHART: PCENT_TWO_FORMATTER, TABLE: PCENT_TWO_FORMATTER },
  ],
  [
    Metric.OrgGrowth,
    { CHART: PCENT_ONE_FORMATTER, TABLE: PCENT_TWO_FORMATTER },
  ],
  [Metric.MktShare, { CHART: PCENT_TWO_FORMATTER, TABLE: PCENT_TWO_FORMATTER }],
  [
    Metric.TotalReturn,
    { CHART: PCENT_TWO_FORMATTER, TABLE: PCENT_TWO_FORMATTER },
  ],
  [Metric.ReturnPtile, { CHART: NO_FORMATTER, TABLE: NO_FORMATTER }],
  [Metric.PrevAUM, { CHART: USD_FORMATTER_CHART, TABLE: USD_FORMATTER_TABLE }],
  [Metric.AvgAUM, { CHART: USD_FORMATTER_CHART, TABLE: USD_FORMATTER_TABLE }],
  [Metric.Netflow, { CHART: USD_FORMATTER_CHART, TABLE: USD_FORMATTER_TABLE }],
])
